<template>
  <div class="batch-container">
    <!-- 顶部工具栏 -->
    <div class="batch-toolbar">
      <div class="toolbar-left">
        <span class="title">批次号写入工具</span>
        <el-tag :type="serialConnected ? 'success' : 'danger'" size="medium" style="margin-left: 12px;">
          {{ serialConnected ? '已连接' : '未连接' }}
        </el-tag>
      </div>
      <div class="toolbar-right">
        <el-button
          :type="serialConnected ? 'danger' : 'success'"
          size="small"
          :icon="serialConnected ? 'el-icon-unlink' : 'el-icon-link'"
          @click="toggleSerialConnection"
          :loading="serialConnecting">
          {{ serialConnected ? '断开' : '连接设备' }}
        </el-button>
      </div>
    </div>

    <!-- 主体区域 -->
    <div class="batch-body">
      <div class="batch-main">
        <el-card shadow="never" class="batch-card">
          <div slot="header" class="card-header">
            <div class="header-left">
              <!-- 左侧：状态文本 -->
              <span class="status-text" :class="statusClass">{{ writeStatusText }}</span>
              <!-- 右侧：进度条 -->
              <div class="header-progress">
                <el-progress
                  v-if="showProgress"
                  :percentage="writeProgress"
                  :status="validProgressStatus"
                  :stroke-width="16"
                  :format="progressFormat"
                  style="flex: 1;" />
              </div>
            </div>
          </div>
          <div class="batch-control" :class="{ 'log-fullscreen': logFullscreen }">
            <!-- 批次号输入 -->
            <div class="batch-input-group">
              <div class="input-row">
                <span class="label">批次号：</span>
                <el-input
                  v-model="batchNo"
                  placeholder="请输入批次号（如：20260710-001）"
                  style="flex: 1;"
                  clearable
                  :disabled="isWriting"
                  @keyup.enter.native="sendBatchNumber" />
              </div>
              <div class="input-row">
                <span class="label">指令前缀：</span>
                <el-input
                  v-model="batchCommand"
                  placeholder="指令前缀（如：SET_BATCH）"
                  style="flex: 1;"
                  :disabled="isWriting" />
              </div>
            </div>

            <!-- 控制行 -->
            <div class="batch-control-row">
              <div class="batch-control-left">
                <div class="separator-group">
                  <span class="label">分隔符：</span>
                  <el-select
                    v-model="commandSeparator"
                    style="width: 100px;"
                    :disabled="isWriting">
                    <el-option label="无" value="" />
                    <el-option label="冒号 (:)" value=":" />
                    <el-option label="等号 (=)" value="=" />
                    <el-option label="空格" value=" " />
                    <el-option label="逗号 (,)" value="," />
                  </el-select>
                  <el-tooltip
                    effect="dark"
                    :content="`${batchCommand}${commandSeparator}${batchNo || '批次号'}`"
                    placement="top"
                    :disabled="`${batchCommand}${commandSeparator}${batchNo || '批次号'}`.length <= 19">
                  <span class="preview-text">
                  预览: {{ batchCommand }}{{ commandSeparator }}{{ batchNo || '批次号' }}
                  </span>
                  </el-tooltip>
                </div>

                <div class="quick-batch">
                  <span class="label">快捷生成：</span>
                  <el-button
                    size="mini"
                    @click="generateBatchNumber('date')"
                    :disabled="isWriting"
                    :type="lastGenerateType === 'date' ? 'primary' : ''">
                    日期
                  </el-button>
                  <el-button
                    size="mini"
                    @click="generateBatchNumber('datetime')"
                    :disabled="isWriting"
                    :type="lastGenerateType === 'datetime' ? 'primary' : ''">
                    日期时间
                  </el-button>
                  <el-button
                    size="mini"
                    @click="generateBatchNumber('random')"
                    :disabled="isWriting"
                    :type="lastGenerateType === 'random' ? 'primary' : ''">
                    随机
                  </el-button>
                  <el-button
                    size="mini"
                    @click="generateBatchNumber('increment')"
                    :disabled="isWriting"
                    :type="lastGenerateType === 'increment' ? 'primary' : ''">
                    递增
                  </el-button>
                </div>
              </div>

              <div class="batch-control-right">
                <el-button
                  v-if="!isWriting"
                  type="primary"
                  class="batch-send-btn"
                  :disabled="!serialConnected || !batchNo"
                  @click="sendBatchNumber">
                  <i class="el-icon-s-promotion"></i>
                  写入批次号
                </el-button>
                <el-button
                  v-else
                  type="danger"
                  class="batch-cancel-btn"
                  @click="cancelWrite">
                  <i class="el-icon-close"></i>
                  取消写入
                </el-button>
              </div>
            </div>

            <!-- 写入记录 -->
            <div class="batch-stats">
              <div class="stats-header">
                <span>写入记录</span>
                <el-button size="mini" type="text" @click="clearHistory" :disabled="isWriting">清空记录</el-button>
              </div>
              <div class="stats-content">
                <div class="stat-item">
                  <span class="stat-label">总写入次数：</span>
                  <span class="stat-value">{{ writeHistory.length }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">最后写入：</span>
                  <span class="stat-value">{{ lastWriteTime || '-' }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">最后批次号：</span>
                  <el-tooltip
                    effect="dark"
                    :content="lastBatchNumber || '-'"
                    placement="top"
                    :disabled="(lastBatchNumber || '-').length <= 15">
                    <span class="stat-value batch-number-display">{{ lastBatchNumber || '-' }}</span>
                  </el-tooltip>
                </div>
              </div>
            </div>

            <!-- 日志 -->
            <div class="log-area" :class="{ 'log-fullscreen': logFullscreen }">
              <div class="log-header">
                <span>操作日志</span>
                <div>
                  <el-button size="mini" type="text" @click="toggleLogFullscreen">
                    <i :class="logFullscreen ? 'el-icon-zoom-out' : 'el-icon-zoom-in'"></i>
                    {{ logFullscreen ? '退出全屏' : '全屏' }}
                  </el-button>
                  <el-button size="mini" type="text" @click="clearLog" :disabled="isWriting">清空</el-button>
                </div>
              </div>
              <div class="log-content" ref="logContainer">
                <div
                  v-for="(log, index) in logs"
                  :key="index"
                  class="log-item"
                  :class="log.type">
                  <span class="log-time">{{ log.time }}</span>
                  <span class="log-message">{{ log.message }}</span>
                </div>
                <div v-if="logs.length === 0" class="log-empty">
                  暂无日志
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

export default {
  name: 'SerialBurnTool',
  data() {
    return {
      // 串口相关
      serialPort: null,
      serialConnected: false,
      serialConnecting: false,
      serialReader: null,
      isReading: false,
      currentWriter: null,
      deviceInfo: null,
      isInitialized: false,

      // 批次号相关
      batchNo: '',
      batchCommand: 'SET_BATCH',
      commandSeparator: '=',
      isWriting: false,
      shouldCancelWrite: false,
      writeProgress: 0,
      writeProgressStatus: '',
      writeStatusText: '准备写入...',
      showProgress: true,

      // 统计
      writeHistory: [],
      lastWriteTime: '',
      lastBatchNumber: '',
      incrementCounter: 0,
      lastGenerateType: '',

      // 日志
      logs: [],
      logFullscreen: false,

      // 缓存 key
      cacheKey: 'serialBurnToolCache',

      // 恢复标志
      isRestoring: false
    }
  },
  computed: {
    ...mapState('serial', ['globalSerialPort', 'globalSerialConnected']),
    ...mapGetters('serial', ['shouldCleanup']),

    validProgressStatus() {
      const status = this.writeProgressStatus
      if (status === 'success' || status === 'exception') {
        return status
      }
      return ''
    },

    // 状态文本颜色类
    statusClass() {
      const text = this.writeStatusText
      if (text === '准备写入...') {
        return 'status-gray'
      }
      if (text === '写入中...') {
        return 'status-blue'
      }
      if (text === '写入完成') {
        return 'status-blue'
      }
      if (text === '已取消' || text === '写入失败' || text === '写入异常') {
        return 'status-red'
      }
      return 'status-gray'
    }
  },
  watch: {
    globalSerialConnected: {
      handler(val) {
        if (val !== this.serialConnected) {
          this.serialConnected = val
          if (val && this.globalSerialPort) {
            this.serialPort = this.globalSerialPort
          }
        }
      },
      immediate: true
    },
    shouldCleanup: {
      handler(val) {
        if (val) {
          this.performFullCleanup()
          this.$store.dispatch('serial/setShouldCleanup', false)
        }
      },
      immediate: true
    },
    // 监听路由参数变化 - 当 URL 中的 batchNo 变化时更新
    '$route.query.batchNo': {
      handler(val) {
        if (val && val.trim() && !this.isWriting) {
          this.batchNo = val.trim()
          this.saveCache()
        }
      },
      immediate: false
    }
  },
  methods: {
    ...mapActions('tabs', ['savePageCache']),
    ...mapActions('serial', [
      'setGlobalSerialPort',
      'setGlobalSerialConnected',
      'setDeviceInfo',
      'setIsReading',
      'setIsWriting',
      'connectSerial',
      'disconnectSerial',
      'cleanupSerialResources',
      'isPortOpen',
      'isSerialHealthy',
      'setSerialReader',
      'setSerialWriter'
    ]),

    saveCache() {
      const cacheData = {
        deviceInfo: this.deviceInfo,
        batchNo: this.batchNo,
        batchCommand: this.batchCommand,
        commandSeparator: this.commandSeparator,
        writeHistory: this.writeHistory,
        lastWriteTime: this.lastWriteTime,
        lastBatchNumber: this.lastBatchNumber,
        incrementCounter: this.incrementCounter,
        logs: this.logs,
        logFullscreen: this.logFullscreen,
        lastGenerateType: this.lastGenerateType,
        serialConnected: this.serialConnected
      }

      this.savePageCache({
        key: this.cacheKey,
        params: cacheData
      })
    },

    restoreCache() {
      const state = this.$store.state
      const cached = state.tabs && state.tabs.pageCache && state.tabs.pageCache[this.cacheKey]
      if (!cached) return false

      const cacheData = cached
      this.deviceInfo = cacheData.deviceInfo || null
      this.batchNo = cacheData.batchNo || ''
      this.batchCommand = cacheData.batchCommand || 'SET_BATCH'
      this.commandSeparator = cacheData.commandSeparator || '='
      this.writeHistory = cacheData.writeHistory || []
      this.lastWriteTime = cacheData.lastWriteTime || ''
      this.lastBatchNumber = cacheData.lastBatchNumber || ''
      this.incrementCounter = cacheData.incrementCounter || 0
      this.logs = cacheData.logs || []
      this.logFullscreen = cacheData.logFullscreen || false
      this.lastGenerateType = cacheData.lastGenerateType || ''

      return true
    },

    async isPortOpen() {
      return await this.$store.dispatch('serial/isPortOpen')
    },

    async cleanupSerialResources(keepPort = false) {
      this.isReading = false
      this.isWriting = false
      this.setIsReading(false)
      this.setIsWriting(false)

      if (this.serialReader) {
        try {
          await this.serialReader.cancel()
        } catch (e) {}
        try {
          this.serialReader.releaseLock()
        } catch (e) {}
        this.serialReader = null
        this.setSerialReader(null)
      }

      if (this.currentWriter) {
        try {
          await this.currentWriter.abort()
        } catch (e) {}
        try {
          this.currentWriter.releaseLock()
        } catch (e) {}
        this.currentWriter = null
        this.setSerialWriter(null)
      }

      await this.$store.dispatch('serial/cleanupSerialResources', keepPort)

      if (!keepPort) {
        this.serialPort = null
        this.serialConnected = false
        this.deviceInfo = null
      }
      return true
    },

    async restoreFromGlobalState() {
      if (this.isRestoring) return
      this.isRestoring = true

      try {
        if (this.globalSerialConnected && this.globalSerialPort) {
          this.serialPort = this.globalSerialPort
          this.serialConnected = true

          const isOpen = await this.isPortOpen()
          if (isOpen && !this.isReading) {
            await new Promise(resolve => setTimeout(resolve, 200))
            this.listenSerialData()
          }
          return true
        }
        return false
      } catch (error) {
        console.error('恢复串口连接失败:', error)
        await this.cleanupSerialResources(false)
        return false
      } finally {
        this.isRestoring = false
      }
    },

    async disconnectSerial() {
      if (this.isWriting) {
        this.$message.warning('正在写入中，请先取消写入')
        return
      }

      try {
        this.isReading = false
        this.setIsReading(false)

        if (this.serialReader) {
          try {
            await this.serialReader.cancel()
          } catch (e) {}
          try {
            this.serialReader.releaseLock()
          } catch (e) {}
          this.serialReader = null
          this.setSerialReader(null)
        }

        if (this.currentWriter) {
          try {
            await this.currentWriter.abort()
          } catch (e) {}
          try {
            this.currentWriter.releaseLock()
          } catch (e) {}
          this.currentWriter = null
          this.setSerialWriter(null)
        }

        await this.$store.dispatch('serial/disconnectSerial')

        this.serialConnected = false
        this.serialPort = null
        this.deviceInfo = null

        this.addLog('info', '串口已断开')
        this.$message.success('串口已断开')
        this.saveCache()
      } catch (error) {
        console.error('断开串口失败:', error)
        this.addLog('error', `断开串口失败: ${error.message}`)
        this.serialConnected = false
        this.serialPort = null
        this.serialReader = null
        this.currentWriter = null
        this.isReading = false
        this.setGlobalSerialConnected(false)
        this.setGlobalSerialPort(null)
        this.$message.warning('串口已强制断开')
      }
    },

    async connectSerial() {
      if (!this.serialPort) {
        const msg = '串口对象为空，请重新选择设备'
        this.addLog('error', msg)
        this.$message.error(msg)
        this.serialConnecting = false
        return false
      }

      if (this.isWriting) {
        this.$message.warning('正在写入中，请稍候')
        return false
      }

      this.serialConnecting = true
      try {
        await this.$store.dispatch('serial/connectSerial', {
          port: this.serialPort,
          deviceInfo: this.deviceInfo
        })

        this.serialConnected = true
        this.addLog('success', '串口连接成功')
        this.$message.success('串口连接成功')
        this.saveCache()

        if (!this.isReading) {
          await new Promise(resolve => setTimeout(resolve, 200))
          this.listenSerialData()
        }
        return true
      } catch (error) {
        console.error('连接串口失败:', error)
        this.addLog('error', `连接串口失败: ${error.message}`)
        this.$message.error(`连接串口失败: ${error.message}`)
        this.serialConnected = false
        this.serialPort = null
        return false
      } finally {
        this.serialConnecting = false
      }
    },

    toggleSerialConnection() {
      if (this.serialConnected) {
        this.disconnectSerial()
      } else {
        this.selectAndConnect()
      }
    },

    async selectAndConnect() {
      if (!navigator.serial) {
        this.addLog('error', '当前浏览器不支持 Web Serial API')
        this.$message.warning('当前浏览器不支持 Web Serial API')
        return
      }

      if (this.serialConnected) {
        this.$message.warning('请先断开当前连接')
        return
      }

      if (this.isWriting) {
        this.$message.warning('正在写入中，请稍候')
        return
      }

      this.serialConnecting = true

      try {
        await this.cleanupSerialResources(false)

        const port = await navigator.serial.requestPort()
        if (!port) {
          this.serialConnecting = false
          return
        }

        const info = port.getInfo()
        this.serialPort = port
        this.deviceInfo = {
          vid: info.usbVendorId,
          pid: info.usbProductId
        }

        await this.connectSerial()

      } catch (error) {
        if (error.name === 'NotFoundError') {
          // 用户取消选择，不记录日志
        } else {
          console.error('选择设备失败:', error)
          this.addLog('error', `选择设备失败: ${error.message}`)
          this.$message.error('选择设备失败')
        }
      } finally {
        this.serialConnecting = false
        this.saveCache()
      }
    },

    async listenSerialData() {
      this.serialPort = this.globalSerialPort

      if (!this.serialPort || !this.serialPort.readable) {
        return
      }

      if (this.serialPort.readable.locked) {
        if (this.serialReader) {
          try {
            await this.serialReader.cancel()
            this.serialReader.releaseLock()
            this.serialReader = null
            this.setSerialReader(null)
          } catch (e) {
            this.addLog('error', `释放读取锁失败: ${e.message}`)
          }
        }
        await new Promise(resolve => setTimeout(resolve, 200))

        if (this.serialPort.readable.locked) {
          try {
            const reader = this.serialPort.readable.getReader()
            reader.releaseLock()
            await new Promise(resolve => setTimeout(resolve, 100))
          } catch (e) {
            this.addLog('error', `强制释放读取锁失败: ${e.message}`)
            return
          }
        }
      }

      if (this.serialPort.readable.locked) {
        this.addLog('error', '读取流仍被锁定，无法启动数据监听')
        return
      }

      if (this.isReading && this.serialReader) {
        return
      }

      this.isReading = true
      this.setIsReading(true)

      try {
        this.serialReader = this.serialPort.readable.getReader()
        this.setSerialReader(this.serialReader)

        while (this.isReading) {
          const { value, done } = await this.serialReader.read()
          if (done) {
            break
          }
          const text = new TextDecoder().decode(value)
          if (text.trim()) {
            this.addLog('receive', text.trim())
          }
        }
      } catch (error) {
        if (error.name !== 'CancelError' && error.name !== 'AbortError') {
          console.error('读取串口数据失败:', error)
          this.addLog('error', `读取串口数据失败: ${error.message}`)
        }
      } finally {
        if (this.serialReader) {
          try {
            this.serialReader.releaseLock()
          } catch (e) {
            console.warn('释放 reader 锁失败:', e)
          }
          this.serialReader = null
          this.setSerialReader(null)
        }
        this.isReading = false
        this.setIsReading(false)
      }
    },

    async writeToSerial(data, addNewline = true, updateProgress = true) {
      if (!this.serialConnected || !this.serialPort) {
        this.addLog('error', '串口未连接')
        return false
      }

      if (this.shouldCancelWrite) {
        return false
      }

      try {
        if (!this.serialPort.writable) {
          this.addLog('error', '串口不可写')
          return false
        }

        this.currentWriter = this.serialPort.writable.getWriter()
        this.setSerialWriter(this.currentWriter)

        const encoded = new TextEncoder().encode(addNewline ? data + '\r\n' : data)

        const chunkSize = 10
        const totalLength = encoded.length

        for (let i = 0; i < totalLength; i += chunkSize) {
          if (this.shouldCancelWrite) {
            if (this.currentWriter) {
              try {
                await this.currentWriter.abort()
              } catch (e) {}
              try {
                this.currentWriter.releaseLock()
              } catch (e) {}
              this.currentWriter = null
              this.setSerialWriter(null)
            }
            return false
          }
          const chunk = encoded.slice(i, Math.min(i + chunkSize, totalLength))
          await this.currentWriter.write(chunk)
          if (updateProgress) {
            this.writeProgress = Math.floor(((i + chunkSize) / totalLength) * 100)
            if (this.writeProgress > 100) this.writeProgress = 100
          }
        }

        if (updateProgress) {
          this.writeProgress = 100
        }
        this.addLog('send', data)
        return true
      } catch (error) {
        console.error('发送数据失败:', error)
        if (error.name !== 'AbortError' && error.name !== 'CancelError') {
          this.addLog('error', `发送数据失败: ${error.message}`)
        }
        return false
      } finally {
        if (this.currentWriter) {
          try {
            this.currentWriter.releaseLock()
          } catch (e) {}
          this.currentWriter = null
          this.setSerialWriter(null)
        }
      }
    },

    generateBatchNumber(type) {
      if (this.isWriting) {
        this.$message.warning('正在写入中，请稍候')
        return
      }

      const now = new Date()
      let result = ''

      switch (type) {
        case 'date':
          result = this.formatDate(now, 'YYYYMMDD')
          break
        case 'datetime':
          result = this.formatDate(now, 'YYYYMMDD-HHmmss')
          break
        case 'random':
          result = this.formatDate(now, 'YYYYMMDD') + '-' + Math.random().toString(36).substr(2, 6).toUpperCase()
          break
        case 'increment':
          this.incrementCounter++
          result = this.formatDate(now, 'YYYYMMDD') + '-' + String(this.incrementCounter).padStart(4, '0')
          break
        default:
          result = ''
      }

      this.batchNo = result
      this.lastGenerateType = type
      this.saveCache()
    },

    formatDate(date, format) {
      const map = {
        'YYYY': date.getFullYear(),
        'MM': String(date.getMonth() + 1).padStart(2, '0'),
        'DD': String(date.getDate()).padStart(2, '0'),
        'HH': String(date.getHours()).padStart(2, '0'),
        'mm': String(date.getMinutes()).padStart(2, '0'),
        'ss': String(date.getSeconds()).padStart(2, '0'),
      }
      return format.replace(/YYYY|MM|DD|HH|mm|ss/g, matched => map[matched])
    },

    clearHistory() {
      if (this.isWriting) {
        this.$message.warning('正在写入中，请稍候')
        return
      }
      this.writeHistory = []
      this.lastWriteTime = ''
      this.lastBatchNumber = ''
      this.saveCache()
    },

    cancelWrite() {
      this.shouldCancelWrite = true
      this.isWriting = false
      this.setIsWriting(false)
      this.writeProgressStatus = 'exception'
      this.writeStatusText = '已取消'
      this.addLog('warning', '用户取消写入')
      this.$message.warning('已取消写入')
    },

    resetProgress() {
      this.showProgress = false
      this.writeProgress = 0
      this.writeProgressStatus = ''
      this.writeStatusText = '准备写入...'

      this.$nextTick(() => {
        this.showProgress = true
      })
    },

    progressFormat(percentage) {
      return percentage + '%'
    },

    async sendBatchNumber() {
      if (!this.serialConnected) {
        this.$message.warning('请先连接串口')
        return
      }

      if (!this.batchNo) {
        this.$message.warning('请输入批次号')
        return
      }

      if (this.isWriting) return

      this.resetProgress()
      await this.$nextTick()

      this.isWriting = true
      this.setIsWriting(true)
      this.shouldCancelWrite = false

      const command = `${this.batchCommand}${this.commandSeparator}${this.batchNo}`
      const currentBatchNo = this.batchNo

      try {
        let progressInterval = null
        let progressValue = 0

        await new Promise(resolve => setTimeout(resolve, 50))

        progressInterval = setInterval(() => {
          if (this.shouldCancelWrite) {
            clearInterval(progressInterval)
            return
          }
          if (progressValue < 90) {
            progressValue += Math.floor(Math.random() * 8) + 2
            if (progressValue > 90) progressValue = 90
            this.writeProgress = progressValue
            this.writeStatusText = '写入中...'
          }
        }, 150)

        const success = await this.writeToSerial(command, true, false)

        if (progressInterval) {
          clearInterval(progressInterval)
          progressInterval = null
        }

        if (this.shouldCancelWrite) {
          this.writeProgressStatus = 'exception'
          this.writeStatusText = '已取消'
          this.isWriting = false
          this.setIsWriting(false)
          setTimeout(() => {
            this.resetProgress()
          }, 500)
          return
        }

        if (success) {
          this.writeProgress = 100
          this.writeProgressStatus = 'success'
          this.writeStatusText = '写入完成'

          this.writeHistory.push({
            batchNo: currentBatchNo,
            time: new Date().toLocaleString(),
            command: command
          })
          this.lastWriteTime = new Date().toLocaleString()
          this.lastBatchNumber = currentBatchNo

          this.addLog('success', `批次号写入成功: ${currentBatchNo}`)
          this.saveCache()

          this.isWriting = false
          this.setIsWriting(false)
        } else {
          this.writeProgressStatus = 'exception'
          this.writeStatusText = '写入失败'
          this.addLog('error', '批次号写入失败')
          this.$message.error('批次号写入失败')
          this.isWriting = false
          this.setIsWriting(false)
        }
      } catch (error) {
        console.error('写入批次号失败:', error)
        this.writeProgressStatus = 'exception'
        this.writeStatusText = '写入异常'
        this.addLog('error', `写入批次号失败: ${error.message}`)
        this.$message.error('写入批次号失败')
        this.isWriting = false
        this.setIsWriting(false)
      }
    },

    toggleLogFullscreen() {
      this.logFullscreen = !this.logFullscreen
      this.saveCache()
      this.$nextTick(() => {
        const container = this.$refs.logContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },

    addLog(type, message) {
      const now = new Date()
      const time = this.formatDate(now, 'YYYY-MM-DD HH:mm:ss') + '.' + String(now.getMilliseconds()).padStart(3, '0')
      this.logs.push({ type, message, time })
      if (this.logs.length > 500) {
        this.logs = this.logs.slice(-500)
      }
      this.saveCache()
      this.$nextTick(() => {
        const container = this.$refs.logContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },

    clearLog() {
      if (this.isWriting) {
        this.$message.warning('正在写入中，请稍候')
        return
      }
      this.logs = []
      this.saveCache()
    },

    async performFullCleanup() {
      console.log('执行串口完整清理...')

      try {
        this.shouldCancelWrite = true
        this.isWriting = false
        this.setIsWriting(false)

        this.isReading = false
        this.setIsReading(false)

        if (this.serialReader) {
          try {
            await this.serialReader.cancel()
          } catch (e) {}
          try {
            this.serialReader.releaseLock()
          } catch (e) {}
          this.serialReader = null
          this.setSerialReader(null)
        }

        if (this.currentWriter) {
          try {
            await this.currentWriter.abort()
          } catch (e) {}
          try {
            this.currentWriter.releaseLock()
          } catch (e) {}
          this.currentWriter = null
          this.setSerialWriter(null)
        }

        if (this.serialPort) {
          try {
            const isOpen = await this.isPortOpen()
            if (isOpen) {
              await this.serialPort.close()
            }
          } catch (e) {
            console.warn('关闭串口失败:', e)
          }
        }

        this.serialConnected = false
        this.serialPort = null
        this.serialReader = null
        this.currentWriter = null
        this.isReading = false
        this.isWriting = false
        this.deviceInfo = null
        this.writeProgress = 0
        this.writeStatusText = '准备写入...'
        this.writeProgressStatus = ''

        this.logs = []
        this.writeHistory = []
        this.lastWriteTime = ''
        this.lastBatchNumber = ''

        this.batchNo = ''
        this.batchCommand = 'SET_BATCH'
        this.commandSeparator = '='
        this.incrementCounter = 0
        this.lastGenerateType = ''

        this.setGlobalSerialConnected(false)
        this.setGlobalSerialPort(null)

        try {
          sessionStorage.removeItem('serialBurnToolCache')
        } catch (e) {}
        console.log('串口完整清理完成')
      } catch (error) {
        console.error('清理串口时出错:', error)
        this.serialConnected = false
        this.serialPort = null
        this.serialReader = null
        this.currentWriter = null
        this.isReading = false
        this.setGlobalSerialConnected(false)
        this.setGlobalSerialPort(null)
      }
    },

    pauseSerialReading() {
      this.isReading = false
      this.setIsReading(false)

      if (this.serialReader) {
        try {
          this.serialReader.cancel()
        } catch (e) {
          console.warn('取消 reader 失败:', e)
        }
        try {
          this.serialReader.releaseLock()
        } catch (e) {
          console.warn('释放 reader 锁失败:', e)
        }
        this.serialReader = null
        this.setSerialReader(null)
      }

      if (this.currentWriter) {
        try {
          this.currentWriter.abort()
        } catch (e) {
          console.warn('abort writer 失败:', e)
        }
        try {
          this.currentWriter.releaseLock()
        } catch (e) {
          console.warn('释放 writer 锁失败:', e)
        }
        this.currentWriter = null
        this.setSerialWriter(null)
      }

      this.saveCache()
    },

    async resumeSerialReading() {
      if (!this.serialConnected || !this.serialPort) {
        return
      }

      const isOpen = await this.isPortOpen()
      if (!isOpen) {
        await this.connectSerial()
        return
      }

      if (this.isReading) {
        return
      }

      try {
        if (this.serialPort.readable && this.serialPort.readable.locked) {
          try {
            const tempReader = this.serialPort.readable.getReader()
            tempReader.releaseLock()
          } catch (e) {
            await this.connectSerial()
            return
          }
        }

        await new Promise(resolve => setTimeout(resolve, 300))
        this.listenSerialData()
      } catch (error) {
        console.error('恢复串口读取失败:', error)
        await this.connectSerial()
      }
    }
  },

  deactivated() {
    this.pauseSerialReading()
  },

  activated() {
    this.$nextTick(() => {
      this.resumeSerialReading()
    })
  },

  async mounted() {
    // ===== 优先从 URL 获取批次号（Vue2 兼容写法）=====
    const batchNoFromQuery = this.$route && this.$route.query && this.$route.query.batchNo
    if (batchNoFromQuery && batchNoFromQuery.trim()) {
      this.batchNo = batchNoFromQuery.trim()
    } else {
      // 只有 URL 没有批次号时，才从缓存恢复
      const restored = this.restoreCache()
      if (!restored) {
        // 首次使用时显示提示
      }
    }

    await this.$nextTick()

    if (this.globalSerialConnected && this.globalSerialPort) {
      this.serialPort = this.globalSerialPort
      const isOpen = await this.isPortOpen()

      if (isOpen) {
        this.serialConnected = true
        if (!this.isReading) {
          await new Promise(resolve => setTimeout(resolve, 200))
          this.listenSerialData()
        }
      } else {
        await this.cleanupSerialResources(false)
        await this.restoreFromGlobalState()
      }
    } else {
      await this.restoreFromGlobalState()
    }

    this.isInitialized = true

    if (this.$bus) {
      this.$bus.$on('logout-cleanup', this.performFullCleanup)
    }
  },

  beforeDestroy() {
    if (this.$bus) {
      this.$bus.$off('logout-cleanup', this.performFullCleanup)
    }

    this.isReading = false
    this.setIsReading(false)

    if (this.serialReader) {
      try {
        this.serialReader.cancel()
      } catch (e) {}
      try {
        this.serialReader.releaseLock()
      } catch (e) {}
      this.serialReader = null
      this.setSerialReader(null)
    }

    if (this.currentWriter) {
      try {
        this.currentWriter.abort()
      } catch (e) {}
      try {
        this.currentWriter.releaseLock()
      } catch (e) {}
      this.currentWriter = null
      this.setSerialWriter(null)
    }

    this.saveCache()
  }
}
</script>

<style lang="less" scoped>
.batch-container {
  height: calc(100vh - 48px);
  padding: 8px;
  background: #f0f2f5;
  display: flex;
  flex-direction: column;
  overflow-y: auto !important;
}

.batch-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  // ===== 允许换行 =====
  flex-wrap: wrap !important;
  gap: 8px 12px; // 行间距和列间距

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap; // 左侧也允许换行

    .title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      white-space: nowrap;
    }
  }

  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap; // 右侧允许换行
  }
}

.batch-body {
  flex: 1;
  display: flex;
  min-height: 0;
}

.batch-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  .batch-card {
    flex: 1;
    display: flex;
    flex-direction: column;

    /deep/ .el-card__body {
      flex: 1;
      overflow: hidden;
      padding: 16px;
      display: flex;
      flex-direction: column;
    }

    /deep/ .el-card__header {
      padding: 10px 16px !important;  // 与 body 的 padding 一致
      border-bottom: 1px solid #ebeef5;
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0;
      border-bottom: none;
      flex-shrink: 0;

      .header-left {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
        padding: 0;

        .status-text {
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
          min-width: 80px;
          transition: color 0.3s ease;

          &.status-gray {
            color: #909399;
          }

          &.status-blue {
            color: #409eff;
          }

          &.status-red {
            color: #f56c6c;
          }
        }

        .header-progress {
          flex: 1;
          display: flex;
          align-items: center;

          /deep/ .el-progress {
            flex: 1;

            .el-progress-bar {
              .el-progress-bar__inner {
                &::after {
                  content: '' !important;
                  display: none !important;
                }
              }
            }

            &.is-success {
              .el-progress-bar {
                .el-progress-bar__inner {
                  &::after {
                    content: '' !important;
                    display: none !important;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

.batch-control {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 10px;
  padding: 10px;
  min-height: 0;

  &.log-fullscreen {
    .log-area {
      flex: 1;
      height: 100%;
    }
  }

  .batch-input-group {
    flex-shrink: 0;
    padding-left: 10px;

    .input-row {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      .label {
        color: #606266;
        font-size: 14px;
        width: 80px;
        flex-shrink: 0;
      }
    }
  }

  .batch-control-row {
    display: flex;
    flex-wrap: wrap !important;
    gap: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    padding: 10px 16px;
    border: 1px solid #ebeef5;
    flex-shrink: 0;

    .batch-control-left {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
      min-width: 200px; // 最小宽度，防止被压缩太狠

      .separator-group {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }

      .quick-batch {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: wrap;
      }
    }

    .batch-control-right {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      margin-left: auto; // 推到右侧

      // 当空间不足时，按钮占满一行
      .batch-send-btn,
      .batch-cancel-btn {
        flex: 1 1 auto;
        min-width: 120px;
      }
    }

    .label {
      color: #606266;
      font-size: 15px;
      white-space: nowrap;
    }

    .preview-text {
      color: #909399;
      font-size: 14px;
      max-width: 280px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: inline-block;
    }
  }

  .batch-send-btn {
    height: 44px !important;
    padding: 0 40px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    border-radius: 6px !important;
    border: 1px solid #409eff !important;
    background: #409eff !important;
    color: #fff !important;
    transition: background 0.2s ease !important;
    min-width: 160px;

    i {
      font-size: 18px !important;
      margin-right: 6px !important;
    }

    &:hover:not(:disabled) {
      background: #66b1ff !important;
      border-color: #66b1ff !important;
    }

    &:active:not(:disabled) {
      background: #3a8ee6 !important;
      border-color: #3a8ee6 !important;
    }

    &:disabled {
      background: #a8c8e8 !important;
      border-color: #a8c8e8 !important;
      cursor: not-allowed;
      opacity: 0.7;
    }
  }

  .batch-cancel-btn {
    height: 44px !important;
    padding: 0 40px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    border-radius: 6px !important;
    border: 1px solid #f56c6c !important;
    background: #f56c6c !important;
    color: #fff !important;
    transition: background 0.2s ease !important;
    min-width: 160px;

    i {
      font-size: 18px !important;
      margin-right: 6px !important;
    }

    &:hover {
      background: #f78989 !important;
      border-color: #f78989 !important;
    }

    &:active {
      background: #e06060 !important;
      border-color: #e06060 !important;
    }
  }

  .batch-stats {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 8px 12px;
    background: #fafafa;
    flex-shrink: 0;

    .stats-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
      font-weight: 500;
      color: #303133;
      font-size: 13px;
    }

    .stats-content {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;

      .stat-item {
        display: flex;
        align-items: center;
        gap: 4px;

        .stat-label {
          color: #909399;
          font-size: 13px;
        }
        .stat-value {
          color: #303133;
          font-size: 13px;
          font-weight: 500;
        }
        &:first-child .stat-value {
          color: #67c23a;
          font-weight: 600;
        }
        .batch-number-display {
          display: inline-block;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          vertical-align: bottom;
        }
      }
    }
  }

  .log-area {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    overflow: hidden;
    transition: all 0.3s ease;

    &.log-fullscreen {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 999;
      border-radius: 0;
      border: none;
      background: #fff;

      .log-content {
        background: #1a1a2e;
        padding: 12px 20px;
      }
    }

    .log-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 12px;
      background: #f5f7fa;
      border-bottom: 1px solid #ebeef5;
      flex-shrink: 0;

      span {
        font-size: 13px;
        font-weight: 500;
        color: #303133;
      }
    }

    .log-content {
      flex: 1;
      overflow-y: auto;
      padding: 6px 12px;
      background: #1a1a2e;
      font-family: 'Courier New', monospace;

      .log-item {
        display: flex;
        gap: 12px;
        padding: 2px 0;
        font-size: 12px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);

        .log-time {
          color: #ffffff;
          flex-shrink: 0;
        }

        .log-message {
          color: #ccc;
          word-break: break-all;
        }

        &.success .log-message {
          color: #67c23a;
        }

        &.error .log-message {
          color: #f56c6c;
        }

        &.warning .log-message {
          color: #e6a23c;
        }

        &.info .log-message {
          color: #409eff;
        }

        &.send .log-message {
          color: #ffd04b;
        }

        &.receive .log-message {
          color: #67c23a;
        }
      }

      .log-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #666;
        font-size: 13px;
      }
    }
  }
}

.el-card {
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  /deep/ .el-card__header {
    padding: 10px 16px;
    border-bottom: 1px solid #ebeef5;
  }
}
</style>

<!-- 全局样式 - 去掉 el-progress 的勾号 -->
<style lang="less">
.el-progress-bar__inner::after {
  display: none !important;
}

.el-progress.is-success .el-progress-bar__inner::after {
  display: none !important;
}

.el-progress .el-progress-bar__innerText {
  font-size: 12px;
  font-weight: 500;
}
</style>
